import {
  flip,
  hide,
  offset,
  shift,
  useFloating,
  UseFloatingOptions,
} from '@floating-ui/react';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import { BasePoint } from 'lib-knowledge-document-structure/slate/package';
import { useLayoutEffect, useRef } from 'react';

import {
  FloatingProps,
  getPointBoundingClientRect,
  useToggleAutoAttach,
} from './hoverUtils';

// TODO: also return getFloatingProps which can just return {}

/**
 * Helps position an floating element near the editor selection.
 *
 * @returns
 */
const usePointHover = ({
  point,
  floatingDivId,
  onOpenChange,
}: {
  point: BasePoint | null;
  floatingDivId: string;
  onOpenChange?: UseFloatingOptions['onOpenChange'];
}): FloatingProps => {
  const editor = useMyEditorRef();
  const isOpen = !!point;
  const virtualElementRef = useRef({
    getBoundingClientRect: getPointBoundingClientRect(editor, point),
  });

  const floatingResult = useFloating({
    middleware: [
      offset(6),
      shift(),
      flip({
        padding: 150,
        fallbackPlacements: ['top-start'],
      }),
      hide({
        padding: {
          top: 64 + 48, // Size of the two heading elements
          left: 0,
          bottom: 0,
          right: 0,
        },
      }),
    ],
    strategy: 'fixed',
    placement: 'bottom-start',
    onOpenChange,
    open: isOpen,
  });

  const { update, refs, middlewareData } = floatingResult;

  useToggleAutoAttach({
    isOpen,
    update,
    floatingDivId,
    virtualElementRef: virtualElementRef.current,
  });

  // Only update if we have a point and are open
  useLayoutEffect(() => {
    if (isOpen && point) {
      virtualElementRef.current = {
        getBoundingClientRect: getPointBoundingClientRect(editor, point),
      };
      refs.setReference(virtualElementRef.current);
    }
  }, [editor, isOpen, point, refs]);

  return {
    ...floatingResult,
    isOpen,
    hide: middlewareData.hide?.referenceHidden || false,
  };
};

export default usePointHover;
